import type { DiscoveredPoi, DiscoveredPoiDetails } from '$lib/components/ui/map/_other/discoveredPoi';
import type { FlatCoordinates } from '$lib/features/_other/coordinates.type';
import { SvelteMap } from 'svelte/reactivity';

export interface TourWikiCacheSnapshot {
    landmarks: [string, DiscoveredPoi][];
    details: [string, DiscoveredPoiDetails][];
    searchedCenters: FlatCoordinates[];
    galleryFetched: string[];
    rejected: string[];
}

export class TourWikiCache {
    /** Reactive: the player's $derived lists read from this. */
    readonly landmarks = new SvelteMap<string, DiscoveredPoi>();

    // Plain bookkeeping, nothing renders from these so no reactivity needed.
    readonly details = new Map<string, DiscoveredPoiDetails>();
    readonly searchedCenters: FlatCoordinates[] = [];
    readonly galleryFetched = new Set<string>(); // media list already requested
    readonly rejected = new Set<string>(); // fetched, but had no usable images
    private readonly inflight = new Map<string, Promise<unknown>>();
    geoSearchInFlight = false;

    constructor(readonly tourSlug: string) { }

    /** Concurrent callers for the same key share one request. */
    dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
        const running = this.inflight.get(key) as Promise<T> | undefined;
        if (running) return running;
        const promise = fn().finally(() => this.inflight.delete(key));
        this.inflight.set(key, promise);
        return promise;
    }

    serialize(): TourWikiCacheSnapshot {
        return {
            landmarks: [...this.landmarks],
            details: [...this.details],
            searchedCenters: [...this.searchedCenters],
            galleryFetched: [...this.galleryFetched],
            rejected: [...this.rejected]
        };
    }

    hydrate(s: TourWikiCacheSnapshot) {
        if (this.landmarks.size > 0 || this.searchedCenters.length > 0) return; // never clobber live data
        for (const [k, v] of s.landmarks) this.landmarks.set(k, v);
        for (const [k, v] of s.details) this.details.set(k, v);
        this.searchedCenters.push(...s.searchedCenters);
        s.galleryFetched.forEach((t) => this.galleryFetched.add(t));
        s.rejected.forEach((t) => this.rejected.add(t));
    }
}